import type { RouteRecordRaw } from 'vue-router'

import buybackEnabled from '@backmarket/nuxt-layer-buyback/app/middleware/buybackEnabled'

import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'

import { ROUTE_NAMES } from './constants'

const { DASHBOARD } = ROUTE_NAMES

export default (): RouteRecordRaw[] => {
  const referFriendDashboardRoute: RouteRecordRaw = {
    path: 'dashboard/refer-friend',
    name: DASHBOARD.KYC.REFER_FRIEND,
    component: () =>
      import('~/scopes/dashboard/refer-a-friend/ReferAFriend.vue'),
    meta: {
      owners: ['bot-squad-lifecycle-front'],
      tracking: { contentGroup: 'Account' },
    },
  }

  const webviewRoutes = generateWebviewRoutes([referFriendDashboardRoute])

  return [
    {
      path: 'dashboard',
      name: DASHBOARD.SELF,
      redirect: { name: DASHBOARD.MY_ORDERS.SELF },
      component: () => import('~/scopes/dashboard/layout/DashboardLayout.vue'),
      children: [
        {
          path: 'profile',
          name: DASHBOARD.KYC.PROFILE,
          component: () => import('~/scopes/dashboard/profile/Profile.vue'),
          meta: {
            owners: ['bot-squad-sales-customer-care-front'],
          },
        },
        {
          path: 'favorites',
          name: DASHBOARD.FAVORITES,
          component: () => import('~/scopes/dashboard/favorites/Favorites.vue'),
          meta: {
            owners: ['bot-squad-lifecycle-front'],
          },
        },
        {
          path: 'others',
          component: () =>
            import('~/scopes/dashboard/my-other-things/MyOtherThings.vue'),
          name: DASHBOARD.KYC.OTHER,
          meta: {
            owners: ['bot-squad-lifecycle-front'],
          },
        },
        {
          path: 'orders',
          name: DASHBOARD.MY_ORDERS.SELF,
          component: () => import('~/scopes/dashboard/my-orders/MyOrders.vue'),
          meta: {
            owners: ['bot-squad-sales-customer-care-front'],
          },
        },
        {
          path: 'sales',
          name: DASHBOARD.MY_SALES,
          component: () => import('~/scopes/dashboard/my-sales/MySales.vue'),
          meta: {
            owners: ['bot-squad-circularity-order-lifecycle-front'],
            middleware: [buybackEnabled],
          },
        },
        {
          path: 'orders/:orderId/orderlines/:orderlineId/insurances/withdraw',
          component: () =>
            import('~/scopes/dashboard/insurances-withdraw/InsurancesWithdraw.vue'),
          name: DASHBOARD.MY_ORDERS.WITHDRAW.INSURANCES,
          meta: {
            disableClientSideNavigation: () => true,
            owners: ['bot-squad-extended-services-front'],
          },
        },
        {
          path: 'refer-friend',
          component: () =>
            import('~/scopes/dashboard/refer-a-friend/ReferAFriend.vue'),
          name: DASHBOARD.KYC.REFER_FRIEND,
          meta: {
            owners: ['bot-squad-lifecycle-front'],
            middleware: [redirectIfWebview],
          },
        },
        {
          path: 'services-hub',
          name: DASHBOARD.SERVICES_HUB,
          component: () =>
            import('~/scopes/dashboard/services-hub/ServicesHub.vue'),
          meta: {
            owners: ['bot-squad-extended-services-front'],
          },
        },
      ],
      meta: {
        auth: {
          required: true,
        },
        owners: ['bot-squad-sales-customer-care-front'],
        tracking: { contentGroup: 'Account' },
      },
    },
    {
      component: () =>
        import('~/scopes/dashboard/profile/DeleteAccount/DeleteAccount.vue'),
      name: DASHBOARD.KYC.DELETE_ACCOUNT,
      path: 'dashboard/profile/delete-account',
      meta: {
        owners: ['bot-squad-identity'],
        auth: {
          required: true,
        },
        tracking: { contentGroup: 'Account' },
      },
    },
    {
      path: 'policy/:policyId',
      name: DASHBOARD.POLICY_DETAILS,
      component: () =>
        import('~/scopes/dashboard/services-hub/pages/PolicyDetails/PolicyDetails.vue'),
      meta: {
        owners: ['bot-squad-extended-services-front'],
        auth: { required: true },
        tracking: { contentGroup: 'Insurance Management' },
      },
    },
    {
      name: DASHBOARD.MY_ORDERS.SELECT_YOUR_ORDERLINE,
      path: 'dashboard/orders/:orderlinePublicId/select-your-orderline',
      component: () =>
        import('~/scopes/dashboard/select-your-orderline/SelectYourOrderline.vue'),
      meta: {
        layout: 'minimal',
        owners: ['bot-squad-sales-customer-care-front'],
        auth: { required: true },
        tracking: { contentGroup: 'Account' },
      },
    },
    {
      name: DASHBOARD.MY_ORDERS.TRACK_PARCEL,
      path: 'dashboard/orders/:orderlinePublicId/track',
      component: () =>
        import('~/scopes/customer-delivery/pages/TrackParcel/TrackParcel.vue'),
      meta: {
        owners: ['bot-squad-delivery-experience-front'],
        auth: { required: true },
        tracking: { contentGroup: 'Account' },
      },
    },
    ...webviewRoutes,
  ]
}
